/**
 * 计费重构（2026-06-28）：持久顶部余额提醒条。
 *
 * 旧版基于 `me.subscription`（试用倒计时 / 订阅过期红条），订阅制废弃后
 * 改为预付费钱包的「低余额提醒」：仅当 `me.wallet.low_balance === true`
 * 时显示一条 amber 横幅，引导用户充值。其它情况渲染 null（余额充足、
 * 钱包未加载等都不打扰）。
 *
 * 保留 24h snooze（localStorage dismiss）—— 低余额是软提醒，用户点掉后
 * 一天内不再弹，避免每次打开应用都被唠叨；下次余额仍低且 snooze 过期再
 * 出现。Strip / BannerBtn / DismissX 子组件样式沿用旧版暖纸色 token。
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MeResponse } from '../api';

interface Props {
  me: MeResponse | null;
  /** Open Settings → Billing (parent owns the modal). */
  onOpenBilling: () => void;
}

const DISMISS_KEY = 'draftlize:paywall_banner_dismissed_until';

export function PaywallBanner({ me, onOpenBilling }: Props) {
  const { t } = useTranslation();

  // Soft-dismiss: stash a timestamp; banner stays hidden until it passes
  // (24h snooze). Initializer reads localStorage so the snooze survives a
  // reload without an effect.
  const [dismissedUntil, setDismissedUntil] = useState<number>(() => {
    try {
      return Number(localStorage.getItem(DISMISS_KEY) ?? '0') || 0;
    } catch {
      return 0;
    }
  });

  // Only nag when the backend flags the wallet as low. Everything else
  // (healthy balance, wallet still loading) shows nothing.
  if (me?.wallet?.low_balance !== true) return null;
  if (Date.now() < dismissedUntil) return null;

  return (
    <Strip>
      <span>
        <strong>{t('billing.lowBalanceTitle')}</strong> — {t('billing.lowBalanceBody')}
      </span>
      <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <BannerBtn onClick={onOpenBilling}>{t('billing.topUp')}</BannerBtn>
        <DismissX
          onClick={() => {
            const until = Date.now() + 86_400_000;
            try {
              localStorage.setItem(DISMISS_KEY, String(until));
            } catch {
              /* ignore */
            }
            setDismissedUntil(until);
          }}
        />
      </span>
    </Strip>
  );
}

function Strip({ children }: { children: React.ReactNode }) {
  // Fixed top strip so the banner needs zero changes to the app's flex-row
  // layout — it overlays the very top edge. Amber treatment (low balance is
  // a soft nudge, never the hard red lockout the old subscription banner
  // had). z-index below the PaywallModal (1000) but above app chrome.
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: '8px 16px',
        background: 'var(--amber-soft)',
        borderBottom: '1px solid var(--amber)',
        fontSize: 13,
        color: 'var(--ink-1)',
        lineHeight: 1.4,
        flexWrap: 'wrap',
        boxShadow: '0 1px 6px rgba(26,24,20,0.10)',
      }}
    >
      {children}
    </div>
  );
}

function BannerBtn({
  children,
  onClick,
  primary,
}: {
  children: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mono"
      style={{
        fontSize: 11,
        letterSpacing: '.1em',
        fontWeight: 700,
        padding: '5px 12px',
        border: '1px solid var(--ink-0)',
        background: primary ? 'var(--ink-0)' : 'transparent',
        color: primary ? 'var(--paper-0)' : 'var(--ink-0)',
        cursor: 'pointer',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function DismissX({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Dismiss"
      style={{
        background: 'transparent',
        border: 'none',
        color: 'var(--ink-3)',
        cursor: 'pointer',
        fontSize: 16,
        lineHeight: 1,
        padding: '0 4px',
        flexShrink: 0,
      }}
    >
      ×
    </button>
  );
}
